// ─────────────────────────────────────────────────────────────────────────────
// «قالب‌ها» on a phone.
//
// Its own render tree, per the mobile concept — one column, full-bleed rows, no
// desktop two-up grid. What it does NOT re-implement is the item EDITOR: that
// dialog is a stack of labelled fields in a scrollable sheet, identical work on
// either surface, and a second copy of it would be a second place for «what a
// template item contains» to drift. It is imported from the desktop kit and
// rendered as-is.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useState } from 'react';
import { C, soft } from '@parsina/framework/clinical/src/constants/theme';
import { Button, EmptyState, SkeletonList } from '@parsina/framework';
import ConfirmDialog from '@parsina/framework/clinical/src/components/common/ConfirmDialog';
import ErrorState from '@parsina/framework/clinical/src/components/common/ErrorState';
import { MobileSettingsCard } from '../../components/mobile/MobileSettingsKit';
import { useOptionalToast } from '@parsina/framework/clinical/src/lib/useOptionalToast';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { useVisitTemplates } from '@parsina/framework/clinical/src/hooks/useVisitTemplates';
import { templateItemCount } from '@parsina/framework/clinical/src/lib/visitTemplates';
import { TemplateEditor } from './web/TemplatesPanel';

const KindSection = ({ kind }) => {
  const { t } = useTranslation();
  const toast = useOptionalToast();
  const store = useVisitTemplates(kind);
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const onSave = useCallback(async (draft) => {
    if (editing?.template) await store.update(editing.template.id, draft);
    else await store.create(draft);
    toast.success(t('settings.templates.saved'));
  }, [editing, store, toast, t]);

  const onConfirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    try {
      await store.remove(pendingDelete.id);
      toast.success(t('settings.templates.deleted'));
    } catch (err) {
      toast.error(t('settings.templates.deleteFailed'));
    } finally { setPendingDelete(null); }
  }, [pendingDelete, store, toast, t]);

  const onDuplicate = useCallback(async (template) => {
    try {
      await store.duplicate(template.id);
      toast.success(t('settings.templates.duplicated'));
    } catch (err) {
      toast.error(t('settings.templates.duplicateFailed'));
    }
  }, [store, toast, t]);

  return (
    <>
      <MobileSettingsCard
        testId={`settings-templates-${kind}`}
        title={t(`settings.templates.${kind}.label`)}
        hint={t(`settings.templates.${kind}.explain`)}
      >
        {store.loading && !store.loaded ? (
          <SkeletonList count={2} avatar={false} trailing={0} label={t('common.state.loading')} />
        ) : store.error && !store.loaded ? (
          <ErrorState
            title={t('settings.templates.loadFailed')}
            error={store.error}
            onRetry={store.reload}
          />
        ) : store.templates.length === 0 ? (
          <EmptyState
            title={t(`settings.templates.${kind}.empty`)}
            style={{ padding: '20px 8px' }}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {store.templates.map((template) => (
              <div
                key={template.id}
                data-testid={`template-row-${kind}`}
                data-template-id={template.id}
                style={{
                  border: `1px solid ${C.border}`, borderRadius: '0.625rem',
                  padding: '0.625rem 0.75rem',
                }}
              >
                <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary }}>
                  {template.title}
                </div>
                <div style={{ fontSize: '0.6875rem', color: C.textMuted, marginTop: 2 }}>
                  {templateItemCount(template)
                    ? t('settings.templates.itemCount', { count: templateItemCount(template) })
                    : t('settings.templates.noItems')}
                </div>
                <div style={{ display: 'flex', gap: '0.375rem', marginTop: '0.5rem' }}>
                  <Button
                    variant="subtle" size="sm"
                    onClick={() => setEditing({ template })}
                    data-testid={`template-edit-${kind}`}
                  >
                    {t('common.actions.edit')}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => onDuplicate(template)}>
                    {t('settings.templates.duplicate')}
                  </Button>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => setPendingDelete(template)}
                    style={{ color: C.danger }}
                  >
                    {t('common.actions.delete')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <Button
          variant="subtle" size="sm"
          onClick={() => setEditing({ template: null })}
          data-testid={`template-new-${kind}`}
          style={{ marginTop: '0.625rem', width: '100%' }}
        >
          + {t(`settings.templates.${kind}.new`)}
        </Button>
      </MobileSettingsCard>

      {editing && (
        <TemplateEditor
          kind={kind}
          open
          template={editing.template}
          onClose={() => setEditing(null)}
          onSave={onSave}
        />
      )}

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title={t('settings.templates.deleteConfirmTitle')}
        message={t('settings.templates.deleteConfirmBody', { title: pendingDelete?.title || '' })}
        confirmLabel={t('common.actions.delete')}
        cancelLabel={t('common.actions.cancel')}
        destructive
        onConfirm={onConfirmDelete}
        onOpenChange={(next) => { if (!next) setPendingDelete(null); }}
      />
    </>
  );
};

export default function MobileTemplatesPanel() {
  const { t } = useTranslation();
  return (
    <div
      data-testid="settings-templates"
      style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
    >
      {/* The same three-part explanation the desktop card gives, condensed:
          what a template is, and — the part that matters — that applying one
          copies rather than links. */}
      <div
        data-testid="settings-templates-intro"
        style={{
          background: soft(C.navy, 0.05), border: `1px solid ${soft(C.navy, 0.16)}`,
          borderRadius: '0.875rem', padding: '0.75rem 0.875rem',
        }}
      >
        <div style={{ fontSize: '0.8125rem', fontWeight: 800, color: C.textPrimary }}>
          {t('settings.templates.intro.title')}
        </div>
        <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: C.textSecondary, lineHeight: 1.9 }}>
          {t('settings.templates.intro.body')}
        </p>
        <p style={{ margin: '0.375rem 0 0', fontSize: '0.75rem', color: C.textSecondary, lineHeight: 1.9 }}>
          {t('settings.templates.intro.copy')}
        </p>
      </div>

      <KindSection kind="medication" />
      <KindSection kind="order" />
    </div>
  );
}
